import { usePostHog } from '@posthog/react';
import CancelButton from 'components/buttons/CancelButton';
import DeleteButton from 'components/buttons/DeleteButton';
import SaveButton from 'components/buttons/SaveButton';
import { useCreateDialogForm, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftSaveMode } from 'components/drafts/DraftSaveMode';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import InfoTooltip from 'components/InfoTooltip';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { cloneElement, useCallback } from 'react';
import { useFormState } from 'react-hook-form';
import { useSelector } from 'store';
import { useAddEffortMutation, useUpdateEffortMutation } from 'store/api/efforts';
import { ClassifierData } from 'types/classifiers';
import { EffortForm } from 'types/efforts';
import { ExpenseDto } from 'types/expenses';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useGoBack, useLeaveForm, useReturnToLocation } from 'utils/useNavigateWithReturnState';
import { useRejectedSubmitCapture } from 'utils/useRejectedSubmitCapture';
import { effortFormToRequest } from './effortFormMapping';
import { ExpensesFields, LinkedEntitiesFields, OverviewFields, PeopleFields } from './form-sections/EffortFields';
import { NominationsFields } from './form-sections/NominationsFields';
import { useEffortForm } from './form-sections/useEffortForm';
import { useConfirmDeleteEffort } from './useConfirmDeleteEffort';

type Props = {
    type: 'add' | 'edit';
    initialData?: EffortForm;
    classifiers: Record<string, ClassifierData>;
    refetch?: () => void;
    expenses: ExpenseDto[];
    fallbackUrl?: string;
    isDeletable?: boolean;
};

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    effortDate: 'effort',
    usedTimeInHours: 'effort',
    effortActivityType: 'effort',
    description: 'effort',
    effortByEmployee: 'effort',
    employees: 'effort',
    partyPersons: 'effort',
    partyCompanies: 'effort',
    matters: 'effort',
    nominations: 'efforts.nominations.title',
    expenses: 'expenses.expenses',
};

const EffortPageForm = ({
    type = 'add',
    initialData,
    classifiers,
    refetch,
    expenses,
    fallbackUrl,
    isDeletable = false,
}: Props): JSX.Element => {
    useFocusedEditMode('efforts');

    const { params, inDialog } = useCreateRoute();
    const { id: effortId } = params;

    const form = useEffortForm({ initialData, classifiers, expenses });
    useCreateDialogForm(form.methods);
    const { control, reset } = form.methods;
    const handleSubmit = useRejectedSubmitCapture(
        form.methods.handleSubmit,
        type === 'edit' ? 'bd-effort-edit' : 'bd-effort'
    );
    // The rows drawn and saved are the hook's own list, which the form's `expenses` follows.
    const { expenseFormDataList, setExpenseFormDataList } = form;
    // A draft keeps its expense lines too.
    const draft = useCreateDraft({
        record: 'bd-effort',
        methods: { handleSubmit, formState: form.methods.formState },
        toPayload: (values: EffortForm) => effortFormToRequest({ ...values, expenses: expenseFormDataList }),
    });
    const { errors } = useFormState({ control });
    const sectionErrors = useSectionErrors(errors, FIELD_TO_SECTION_MAP);

    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const returnTo = useReturnToLocation(fallbackUrl ?? '/efforts');
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();

    const posthog = usePostHog();
    const [addEffort] = useAddEffortMutation();
    const [updateEffort] = useUpdateEffortMutation();
    const confirmDelete = useConfirmDeleteEffort();

    const cancelEditing = useCallback(() => {
        reset();
        goBack();
        setExpenseFormDataList(expenses);
    }, [reset, goBack, expenses, setExpenseFormDataList]);

    const onSubmit = async (formData: EffortForm, action: 'create' | 'update'): Promise<void> => {
        try {
            const data = effortFormToRequest({ ...formData, expenses: expenseFormDataList });

            if (action === 'create') {
                // A draft is created by completing it: the effort keeps the draft's id.
                const creation: Promise<{ id: number }> =
                    draft.draftId === null ? addEffort({ ...data }).unwrap() : draft.complete(data);
                await creation
                    .then((created) => {
                        posthog.capture('effort_created', { activity_type_code: data.effortActivityTypeCode });
                        // The dialog stays on the page it was opened over, so it finishes with the new effort instead.
                        leaveForm(inDialog ? `/effort/${created.id}` : fallbackUrl ? fallbackUrl : '/efforts');
                    })
                    .catch(console.error);
            } else if (action === 'update' && effortId && !isNaN(Number(effortId))) {
                await updateEffort({
                    ...data,
                    id: effortId,
                })
                    .unwrap()
                    .then(() => {
                        if (refetch) refetch();
                        leaveForm(`/effort/${effortId}`);
                    })
                    .catch(console.error);
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitCreateEffort = () => {
        handleSubmit((data) => onSubmit(data, 'create'))();
    };

    const submitUpdateEffort = () => {
        handleSubmit((data) => onSubmit(data, 'update'))();
    };

    const handleDeleteEffort = () => {
        if (effortId && !isNaN(Number(effortId))) {
            confirmDelete(effortId);
        }
    };

    // Show inital name if is edit form. Dont do dynamically to prevent unneeded rerenders
    const getFormTitle = () => {
        switch (type) {
            case 'edit':
                return `${t('efforts.bd-effort')}`;
            default:
                return t('efforts.add-new-effort');
        }
    };

    const getFormHeader = () => {
        const actions: JSX.Element[] = [];

        if (type === 'add') {
            actions.push(
                <CancelButton onClick={cancelEditing} />,
                ...(draft.button ? [draft.button] : []),
                <SaveButton permissions={['CREATE.EFFORTS']} onClick={submitCreateEffort} disabled={buttonsDisabled} />
            );
        } else if (type === 'edit' && effortId) {
            actions.push(
                <CancelButton onClick={cancelEditing} />,
                <DeleteButton
                    permissions={['DELETE.EFFORTS.ALL', 'DELETE.EFFORTS.MY_OWN']}
                    onClick={handleDeleteEffort}
                    disabled={buttonsDisabled || !isDeletable}
                />,
                <SaveButton
                    permissions={['UPDATE.EFFORTS.ALL', 'UPDATE.EFFORTS.MY_OWN']}
                    onClick={submitUpdateEffort}
                    disabled={buttonsDisabled}
                />
            );
        }

        return (
            <ActionHeader
                title={getFormTitle()}
                actions={actions.map((button, index) => cloneElement(button, { key: `action#${index}` }))}
                onBack={goBack}
                draft={draft.detail}
            />
        );
    };

    return (
        <SectionActionsProvider>
            <HighlightedFieldsProvider>
                <FormWrapper>
                    {getFormHeader()}
                    <DraftSaveMode mode={draft.mode}>
                        <SectionLayout sectionErrors={sectionErrors}>
                            <Section title="effort" collapsible>
                                <OverviewFields form={form} />
                                <PeopleFields form={form} />
                                <LinkedEntitiesFields form={form} />
                            </Section>
                            <Section
                                title="efforts.nominations.title"
                                isHidden={!form.showsNominations}
                                // The dialog draws a section's title as its toggle, which cannot hold the tooltip.
                                tooltip={
                                    inDialog ? undefined : <InfoTooltip text="efforts.nominations.title-tooltip" />
                                }
                                collapsible
                            >
                                <NominationsFields form={form} />
                            </Section>
                            <Section title="expenses.expenses" collapsible>
                                <ExpensesFields form={form} />
                            </Section>
                        </SectionLayout>
                    </DraftSaveMode>
                </FormWrapper>
            </HighlightedFieldsProvider>
        </SectionActionsProvider>
    );
};

export default EffortPageForm;
